import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import {Box, Button} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {del, get, post} from "../../common/network";
import useSweetAlert from "../../components/common/alert/useSweetAlert";
import FallbackGeneratedFormMessage from "../../components/common/fallback-form-message/FallbackGeneratedFormMessage";
import FormSearchFilter from "../../components/common/form-search-filter/FormSearchFilter";
import DownloadTypeModal from "../../components/common/modal-preview/DownloadTypeModal";
import MuiTableWrapper from "../../components/common/mui-table-wrapper/MuiTableWrapper";
import {applicantFormCodes, assigneeTenants, inventorFormCodes, tenant} from "../../constants";
import {DateFormatter} from "../../constants/date_format";
import MatterDetailsModal from "../matters/matter-details/MatterDetailsModal";
import MultiFilter from "../../components/multi-filter/Multifilter";
import { getNestedValue, normalizeDate } from "../../utils/CommonMethods";


const Drafts = () => {
    const navigate = useNavigate();
    const [selectedRows, setSelectedRows] = useState([]); // Array of the selected rows
    const [isMatterDetailsOpen, setIsMatterDetailsOpen] = useState(false);
    const [isRowId, setIsRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [formTypeValue, setFormTypeValue] = useState("");
    const [dateValue, setDateValue] = useState("");
    const [selectType, setSelectType] = useState(null);
    const [downloadTypeModal, setDownloadTypeModal] = useState(false);
    const [rowsSelection, setRowsSelection] = useState([]);
    const [dynamicFilters, setDynamicFilters] = useState([
        { column: '', operator: '', value: [], from: null, to: null }
    ]);
    const [searchResetKey, setSearchResetKey] = useState(0);
    const [filteredData, setFilteredData] = useState([]);

    const sweetAlert = useSweetAlert();

    const isAdmin = localStorage.getItem("TENANT_ROLE") === "admin";

    const multiFilterColumns = [
        {
            field: "generated_form_matter.host_file_number",
            headerName: "Docket No.",
            type: "text",
        },
        {
            field: "generated_form.form_code",
            headerName: "Form Code",
            type: "text",
        },
        {
            field: "generated_form_matter.application_number",
            headerName: "Application No.",
            type: "text",
        },
        {
            field: "updated_at",
            headerName: "Updated On",
            type: "date",
        },
    ];

    // HANDLE DELETE FUNCTION
    const handleDelete = () => {
        sweetAlert({
            text: "Are you sure you want to delete the selected form(s)?",
            icon: "warning",
            cancelButtonText: "No",
            confirmButtonText: "Yes"
        }).then(result => {
            if (result.isConfirmed) {
                deleteMatter(result);
            }
        });
    };
    
    const applyMultiFilters = (rows, filters) => {
        if (!filters || !filters.length) return rows;

        return rows.filter(row =>
            filters.every(filter => {
                const { column, operator, value, from, to } = filter;
                // const rowValue = row[column];
                const rowValue = getNestedValue(row, column);

                // EMPTY CHECKS
                if (operator === 'isEmpty') {
                    return rowValue === null || rowValue === undefined || rowValue === '';
                }

                if (operator === 'isNotEmpty') {
                    return rowValue !== null && rowValue !== undefined && rowValue !== '';
                }

                // DATE RANGE
                if ((operator === 'equals' || operator === 'notEquals') && from) {
                    if (!rowValue || !from) return false;

                    const rowDate = new Date(rowValue).toISOString().split('T')[0];
                    const filterDate = new Date(from).toISOString().split('T')[0];

                    return operator === 'equals'
                        ? rowDate === filterDate
                        : rowDate !== filterDate;
                }

                if (operator === 'between') {
                    if (!rowValue || !from || !to) return false;

                    const rowDate = new Date(rowValue);

                    const startDate = new Date(from);
                    startDate.setHours(0, 0, 0, 0);

                    const endDate = new Date(to);
                    endDate.setHours(23, 59, 59, 999); 

                    return rowDate >= startDate && rowDate <= endDate;
                }

                // MULTI-SELECT DROPDOWN
                if (Array.isArray(value) && value.length > 0) {
                    if (Array.isArray(rowValue)) {
                        if (operator === 'doesNotContains' || operator === 'notEquals') {
                        return !rowValue.some(v => value.includes(v));
                        }
                        return rowValue.some(v => value.includes(v));
                    }

                    if (operator === 'doesNotContains' || operator === 'notEquals') {
                        return !value.includes(rowValue);
                    }

                    return value.includes(rowValue);
                }
                
                // If value array is empty, show all rows
                if (Array.isArray(value) && value.length === 0) {
                    return true;
                }

                // TEXT COMPARISON
                const cell = String(rowValue ?? '').toLowerCase();
                const search = String(value ?? '').toLowerCase();

                switch (operator) {
                    case 'contains':
                        return cell.includes(search);
                    case 'doesNotContains':
                        return !cell.includes(search);
                    case 'equals':
                        return cell === search;
                    case 'notEquals':
                        return cell !== search;
                    case 'startsWith':
                        return cell.startsWith(search);
                    case 'endsWith':
                        return cell.endsWith(search);
                    default:
                        return true;
                }
            })
        );
    };

    const handleApplyFilters = (filters) => {
        // Clear single search state
        setSearchValue("");
        setFilterText("");
        setFormTypeValue("");
        setDateValue("");
        setSearchResetKey(prev => prev+1);
        // Store active multi-filters
        setDynamicFilters(filters);

        const normalizedFilters = filters.map(f => ({
            ...f,
            from: normalizeDate(f.from),
            to: normalizeDate(f.to)
        }));
        const result = applyMultiFilters(data, normalizedFilters);
        setFilteredData(result);
    };
    // HANDLE DELETE FUNCTION

    const deleteMatter = async () => {
        try {
            const selectedIds = [];
            for (const row of selectedRows) {
                selectedIds.push(row.id);
            }
            const response = await del(`docgen/generate-forms`, {
                formIds: selectedIds
            });
            if (response?.success === true) {
                toast.success(response.message);
                fetchData();
            }
        } catch (error) {
        } finally {
            setSelectedRows([]);
            setRowsSelection([]);
        }
    };

    const handlePreviewRedirect = () => {
        const queryParams = new URLSearchParams();
        const selectedIds = [];
        for (const row of selectedRows) {
            selectedIds.push(row.id);
        }
        selectedIds.forEach((id) => queryParams.append("id", id));
        window.open(`/docgen/drafts/preview?` + queryParams);
    };

    const handleRedirect = () => {
        const queryParams = new URLSearchParams();
        const selectedIds = [];
        for (const row of selectedRows) {
            selectedIds.push(row.id);
        }
        selectedIds.forEach((id) => queryParams.append("id", id));
        window.open(`/docgen/drafts/modify?` + queryParams);
    };

    // HANDLE DOWNLOAD FUNCTION
    const handleDownload = () => {
        const isCustomForm = selectedRows?.some((form) => form?.generated_form?.form_type === "custom");
        if (isCustomForm) {
            setDownloadTypeModal(true);
        } else {
            downloadForms();
        }
    };
    // HANDLE DOWNLOAD FUNCTION

    //call download api
    const downloadForms = async () => {
        try {
            setIsLoading(true);
            const selectedIds = [];
            for (const row of selectedRows) {
                selectedIds.push(row.id);
            }
            const response = await post(
                `docgen/generated-forms/download`,
                {
                    form_id: selectedIds,
                    ...(selectType ? { downloadType: selectType } : {})
                },
                {
                    responseType: "blob" // Specify that you expect a Blob response
                },
                false
            );

            // Get the current timestamp
            const timestamp = new Date().getTime();

            const contentDispositionHeader =
                response?.headers["content-disposition"];
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(contentDispositionHeader);
            let suggestedFilename;
            if (selectedIds.length !== 1) {
                suggestedFilename = `forms-${timestamp}.zip`;
            } else {
                suggestedFilename = matches && matches[1] ? matches[1].replace(/['"]/g, "") : `Custom Form.${selectType || "pdf"}`;
            }

            // Create a Blob from the response content
            const mimeType = response?.headers["content-type"];
            const blob = new Blob([response.data], {type: mimeType});

            // Create a URL for the blob response
            const url = URL.createObjectURL(blob);

            // Create a hidden anchor element
            const anchor = document.createElement("a");
            anchor.style.display = "none";
            anchor.href = url;
            anchor.download = suggestedFilename; // Set the desired file name

            // Append the anchor to the document body
            document.body.appendChild(anchor);

            // Simulate a click on the anchor to trigger the download
            anchor.click();

            // Remove the anchor from the document body
            document.body.removeChild(anchor);

        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    //#endregion

    //#region for column declaration
    const columns = [
        {
            field: "host_file_number",
            header: "Docket No.",
            render: (value, row) => {
                return (
                    <div
                        style={{textDecoration: "underline", cursor: "pointer", paddingTop: "1%", paddingBottom: "1%"}}
                        onClick={() => {
                            setIsMatterDetailsOpen(true);
                            setIsRowId(row?.generated_form_matter?.id);
                        }}
                    >
                        {row?.generated_form_matter?.host_file_number}
                    </div>
                );
            }
        },
        {
            field: "form_code",
            header: "Form Code",
            render: (value, row) => {
                if (inventorFormCodes.includes(row?.generated_form?.form_code)) {
                    const inventorList = row?.generated_form_matter?.matter_parties?.filter(matter => matter?.party_type === "Inventor")?.map(contact => contact?.party?.name);
                    const selectedInventor = inventorList?.find(inventor => row.contactName === inventor);
                    if (selectedInventor) {
                        return `${row?.generated_form?.form_code}_${selectedInventor}`;
                    }
                } else if (applicantFormCodes.includes(row?.generated_form?.form_code)) {
                    const applicantList = row?.generated_form_matter?.matter_parties?.filter(matter => matter?.party_type === (assigneeTenants.has(tenant) ? "Assignee" : "Applicant"))?.map(contact => contact?.party?.name);
                    const selectedApplicant = applicantList?.find(applicant => row.contactName === applicant);
                    if (selectedApplicant) {
                        return `${row?.generated_form?.form_code}_${selectedApplicant}`;
                    }
                }
                return row?.generated_form?.form_code;
            }
        },
        {
            field: "application_number",
            header: "Application No.",
            render: (value, row) => {
                return row?.generated_form_matter?.application_number;
            }
        },
        {
            field: "status",
            header: "Status"
        },
        {
            field: "updated_at",
            header: "Updated On",
            render: (value) => {
                const date = new Date(value); // Convert the ISO date string to a Date object
                return DateFormatter(date);
            }
        }
    ];

    const handleSearch = value => {
        setDynamicFilters([{ column: '', operator: '', value: [], from: null, to: null }]);
        const columnName = value?.value || ""; // Name of the column
        const searchValue = value?.searchedText || "";
        const dropdownValue = value?.dropdownValue || "";
        setSearchValue(searchValue);
        setFilterText(columnName);
        setFormTypeValue(dropdownValue);

        if (columnName === "updated_at") {
            setDateValue(value);
        }
        setFilteredData(data);
    };

    const fetchData = async () => {
        setIsFetching(true);
        try {

            const response = await get(
                `docgen/generate-forms`
            );
            const draftForms = response?.data?.filter((item) => item.status === "Draft");
            setData(draftForms);
        } catch (error) {
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);
    //#endregion
    useEffect(() => {
        setFilteredData(data);
    }, [data]);


    return (
        <div className="page-content-wrapper">
            <Box className="w-100">
                <header className="section-heading">
                    <div className="left-area">
                        <FormSearchFilter
                            options={[
                                {value: "all", label: "All"},
                                {value: "host_file_number", label: "Docket No."},
                                {value: "form_code", label: "Form Code"},
                                {value: "application_number", label: "Application No."},
                                {value: "updated_at", label: "Updated On"}
                            ]}
                            dateFiltersOptionsValueArray={["updated_at"]}
                            onSearch={(values) => handleSearch(values)}
                            searchResetKey={searchResetKey}
                        />
                    </div>
                    <div className="right-area">
                        <section className="top-actions-section">
                            <div className="buttons-group">
                                <ul>
                                {selectedRows.length > 0 && (
                                    <>
                                        <li>
                                            <Button type="button" onClick={handleDelete}>
                                                <div className="icon-area">
                                                    <DeleteOutlineOutlinedIcon/>
                                                </div>
                                                <p className="text-area">Delete</p>
                                            </Button>
                                        </li>
                                        <li>
                                            <Button
                                                type="button"
                                                onClick={() => handlePreviewRedirect()}
                                            >
                                                <div className="icon-area">
                                                    <RemoveRedEyeOutlinedIcon/>
                                                </div>
                                                <p className="text-area">Preview</p>
                                            </Button>
                                        </li>
                                        <li>
                                            <Button type="button" onClick={() => handleRedirect()}>
                                                <div className="icon-area">
                                                    <EditNoteOutlinedIcon/>
                                                </div>
                                                <p className="text-area">Modify</p>
                                            </Button>
                                        </li>
                                        <li>
                                            <Button type="button" onClick={() => handleDownload()}>
                                                {isLoading ? <CircularProgress sx={{color: "blue", marginLeft: "5%"}}/> :
                                                    <>
                                                        <div className="icon-area">
                                                            <FileDownloadOutlinedIcon/>
                                                        </div>
                                                        <p className="text-area">Download</p>
                                                    </>}
                                            </Button>
                                        </li>
                                    </>
                                )}
                                    {isAdmin && (
                                    <li style={{ marginRight: "10px" }}>
                                        <Button
                                            size="small"
                                            variant="text"
                                            component="label"
                                            onClick={() => navigate("/docgen/analytics-dashboard")}
                                            sx={{
                                                color: "#fff !important",
                                                textTransform: "none",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "6px",
                                                "& svg": { color: "#fff !important" }
                                            }}
                                            className="btn-theme"
                                        >
                                            <AnalyticsOutlinedIcon /> Analytics
                                        </Button>
                                    </li>
                                    )}
                                    <li>
                                        <MultiFilter
                                            filters={dynamicFilters}
                                            setFilters={setDynamicFilters}
                                            columns={multiFilterColumns}
                                            onAddFilter={handleApplyFilters} 
                                        />
                                    </li>
                                </ul>
                            </div>
                        </section>
                    </div>
                </header>
                <MuiTableWrapper
                    columns={columns}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    emptyBody={
                        <FallbackGeneratedFormMessage searchedKeyword={searchValue}/>
                    }
                    searchValue={searchValue}
                    filterColumn={filterText}
                    formTypeValue={formTypeValue}
                    data={filteredData}
                    isFetching={isFetching}
                    dateValue={dateValue}
                    rowsSelection={rowsSelection}
                    setRowsSelection={setRowsSelection}
                />
            </Box>

            {
                <MatterDetailsModal
                    open={isMatterDetailsOpen}
                    handleClose={() => setIsMatterDetailsOpen(false)}
                    id={isRowId}
                />
            }

            {
                <DownloadTypeModal
                    downloadTypeModal={downloadTypeModal}
                    setDownloadTypeModal={setDownloadTypeModal}
                    selectType={selectType}
                    setSelectType={setSelectType}
                    downloadForms={downloadForms}
                    selectedRows={selectedRows}
                />
            }
        </div>
    );
};

export default Drafts;